import { PropsWithChildren } from 'react';
import { compact } from 'lodash';
import { ActionMenu } from 'ui/actionMenu';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { ChevronDown16 } from 'ui/icons';
import { Tooltip } from 'ui/tooltip';
import { useTranslation } from '../../hooks';
import { useListDetailsContext } from '../../contexts/listDetails';
import { useModalsContext } from '../../contexts/modals';
import { usePermissionsContext } from '../../contexts/permissions';
import { StyledActionMenuButton } from './Styled';
import { SubmitType } from '../../types';
import { FORM_ID } from '../../constants';
import { useSegmentationQueryContext } from '../../contexts/segmentationQuery';

export const testIds = {
  actBtnAdd: 'segmentation-uam-act-btn-add',
  actBtnDelete: 'segmentation-uam-act-btn-delete',
  actBtnRemove: 'segmentation-uam-act-btn-remove',
  actMenuUpdate: 'segmentation-uam-act-menu-update',
};

// Available in the SidePanel when a static list is selected (pageVariation === PAGE_VARIATIONS.STATIC)

export const UpdateActionMenu = ({
  variant = BUTTON_VARIANTS.SECONDARY,
}: { variant?: BUTTON_VARIANTS }) => {
  const { t } = useTranslation();
  const { canManageSegmentation, canManageUserProfiles } = usePermissionsContext();
  const { list } = useListDetailsContext();
  const { isValidQuery } = useSegmentationQueryContext();
  const { showDeleteUsersInListModal } = useModalsContext();
  const options: ActionMenuOption[] = compact([
    {
      as: AllowedActionMenuElements.Link,
      content: t('actionMenu.update.btnAddUsers'),
      href: `/lists/update?emailListId=${list.id}`, // Angular route
      id: testIds.actBtnAdd,
      isDisabled: !canManageSegmentation || !canManageUserProfiles,
      Wrapper: ({ children }: PropsWithChildren) => (
        <Tooltip
          isEnabled={!canManageSegmentation || !canManageUserProfiles}
          tooltipContent={t(
            `permissions.${!canManageSegmentation ? 'manageSegmentation' : 'manageUserProfiles'}`,
          )}
        >
          {children}
        </Tooltip>
      ),
    },
    {
      as: AllowedActionMenuElements.Link,
      content: t('actionMenu.update.btnRemoveUsers'),
      href: `/lists/unsub?emailListId=${list.id}`, // Angular route
      id: testIds.actBtnRemove,
      isDisabled: !canManageSegmentation,
      Wrapper: ({ children }: PropsWithChildren) => (
        <Tooltip
          isEnabled={!canManageSegmentation}
          tooltipContent={t(`permissions.manageSegmentation`)}
        >
          {children}
        </Tooltip>
      ),
    },
    {
      as: AllowedActionMenuElements.Button,
      content: t('actionMenu.update.btnDeleteUsersInList'),
      id: testIds.actBtnDelete,
      isDestructive: true,
      isDisabled: !canManageUserProfiles,
      onClick: showDeleteUsersInListModal,
      Wrapper: ({ children }: PropsWithChildren) => (
        <Tooltip
          isEnabled={!canManageUserProfiles}
          tooltipContent={t(`permissions.manageUserProfiles`)}
        >
          {children}
        </Tooltip>
      ),
    },
  ]);

  // Differentiates between the variant in the side panel and the one in the filter header
  const actionMenuTestId = `${testIds.actMenuUpdate}${variant !== BUTTON_VARIANTS.SECONDARY ? `-${variant}` : ''}`;

  return (
    <Tooltip isEnabled={!isValidQuery} tooltipContent={t('actionMenu.disabled')}>
      <ActionMenu
        align={MENU_ALIGNMENTS.RIGHT}
        data-test={actionMenuTestId}
        options={options}
        isDisabled={!isValidQuery}
        style={{ width: '100%' }}
      >
        <StyledActionMenuButton
          variant={variant}
          size={BUTTON_SIZES.SMALL}
          type="submit"
          form={FORM_ID}
          id={SubmitType.save}
        >
          <span>{t('actionMenu.update.label')}</span>
          <ChevronDown16 />
        </StyledActionMenuButton>
      </ActionMenu>
    </Tooltip>
  );
};
